import { v4 as uuidV4 } from 'uuid'

import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'

import { DataModelDraft, DataModelDraftItem } from '../../../types/ai.types'
import { DataModelItemStatus } from '../../designer/dictionary/designer.dictionary'

export type DeveloperDataModelChangesOutcome = {
  keys_to_create?: { key: string; type: string; properties?: Record<string, unknown> }[]
  types_to_instantiate?: {
    key: string
    isProjectReference?: boolean
    targetResourceDefinitionId?: string
    appId?: string
    projectTypeName?: string
  }[]
  reasoning?: string
}

// The agent emits attribute types in its own uppercase vocabulary; coerce to the FE enum
// (NUMBER -> FLOAT, DATE -> DATE_TIME, ARRAY -> OBJECT). Valid enum values pass through; the rest
// default to STRING.
const DEVELOPER_TYPE_ALIASES: Record<string, DataModelAttributeType> = {
  NUMBER: DataModelAttributeType.FLOAT,
  DATE: DataModelAttributeType.DATE_TIME,
  ARRAY: DataModelAttributeType.OBJECT,
}

const coerceAttributeType = (raw: string | undefined): DataModelAttributeType => {
  const token = (raw ?? '').toUpperCase()
  if (DEVELOPER_TYPE_ALIASES[token]) {
    return DEVELOPER_TYPE_ALIASES[token]
  }
  return (Object.values(DataModelAttributeType) as string[]).includes(token)
    ? (token as DataModelAttributeType)
    : DataModelAttributeType.STRING
}

const baseDraftItem = (key: string, type: DataModelAttributeType): DataModelDraftItem => ({
  id: uuidV4(),
  parentDataModelId: null,
  label: key,
  key,
  type,
  properties: {},
  multiInstance: false,
})

export const parseDeveloperDataModelChanges = (
  content: string | null | undefined
): DeveloperDataModelChangesOutcome | null => {
  if (!content) {
    return null
  }
  try {
    const parsed = JSON.parse(content)
    const isDataModelChanges =
      parsed &&
      typeof parsed === 'object' &&
      ('keys_to_create' in parsed || 'types_to_instantiate' in parsed)
    return isDataModelChanges ? (parsed as DeveloperDataModelChangesOutcome) : null
  } catch {
    return null
  }
}

// Map the developer `data_model_changes` outcome to the shared DataModelDraft: new keys go to
// `new[]` (path-based CREATE), project/library types to `referenced[]` (OBJECT reference). The agent
// only ever emits NEW changes, so `existing[]` is always empty.
export const mapDeveloperDataModelChanges = (
  changes: DeveloperDataModelChangesOutcome | null
): DataModelDraft | null => {
  if (!changes) {
    return null
  }

  const newItems: DataModelDraftItem[] = (changes.keys_to_create ?? [])
    .filter((entry) => !!entry?.key)
    .map((entry) => ({
      ...baseDraftItem(entry.key, coerceAttributeType(entry.type)),
      state: DataModelItemStatus.CREATED,
      properties: entry.properties ?? {},
    }))

  const referencedItems: DataModelDraftItem[] = (changes.types_to_instantiate ?? [])
    .filter((entry) => !!entry?.key)
    .map((entry) => ({
      ...baseDraftItem(entry.key, DataModelAttributeType.OBJECT),
      state: DataModelItemStatus.REFERENCED,
      targetResourceDefinitionId: entry.targetResourceDefinitionId,
      appId: entry.appId,
      referenceAppInfo: entry.appId
        ? {
            appId: entry.appId,
            appName: entry.projectTypeName ?? '',
            isCurrentApp: !!entry.isProjectReference,
          }
        : undefined,
    }))

  if (!newItems.length && !referencedItems.length) {
    return null
  }

  return { new: newItems, existing: [], referenced: referencedItems }
}

export const developerDataModelChangesToDraft = (
  content: string | null | undefined
): DataModelDraft | null => mapDeveloperDataModelChanges(parseDeveloperDataModelChanges(content))

// Structural slice of a process data-model entity (`selectResourceDataModelEntities`): its root flag,
// the flowxUuid a reference targets, and each attribute's name and reference — to another local
// entity (targetFlowxUuid) or to a project data type (targetResourceDefinitionId).
export type ProcessDmEntity = {
  root?: boolean
  flowxUuid?: string
  attributes?: {
    name?: string
    reference?: { targetFlowxUuid?: string | null; targetResourceDefinitionId?: string | null } | null
  }[]
}

type ProcessDmMatch = {
  // The key is already in the model (or sits under a project reference), so it must not be created.
  present: boolean
  // A project reference lies on the key's path, so the key belongs to the project data model.
  projectSourced: boolean
}

const NO_MATCH: ProcessDmMatch = { present: false, projectSourced: false }

// Walk a draft key's path from the root, following each attribute's reference, to see how it relates
// to the process data model.
const matchAgainstProcessDm = (
  item: DataModelDraftItem,
  entities: ProcessDmEntity[]
): ProcessDmMatch => {
  const segments = item.key.split('.')
  const leafName = segments[segments.length - 1]

  let entity = entities.find((candidate) => candidate.root)
  for (const parentName of segments.slice(0, -1)) {
    const parent = entity?.attributes?.find((attribute) => attribute.name === parentName)
    if (!parent) {
      return NO_MATCH
    }
    // A project reference's children live in the referenced type and can't be added locally.
    if (parent.reference?.targetResourceDefinitionId) {
      return { present: true, projectSourced: true }
    }
    const childFlowxUuid = parent.reference?.targetFlowxUuid
    entity = childFlowxUuid
      ? entities.find((candidate) => candidate.flowxUuid === childFlowxUuid)
      : undefined
    if (!entity) {
      return NO_MATCH
    }
  }

  const leafAttribute = entity?.attributes?.find((attribute) => attribute.name === leafName)
  if (!leafAttribute) {
    return NO_MATCH
  }
  return { present: true, projectSourced: !!leafAttribute.reference?.targetResourceDefinitionId }
}

/**
 * Reconcile the developer agent's additive draft against the current process data model. A key
 * already in the model — directly, or under a project reference whose children can't be added
 * locally — moves to `existing[]` (shown as reused, skipped on apply); the rest stay in
 * `new[]`/`referenced[]` to be created. Matching by resolved path keeps a nested key scoped to its
 * parent entity, and existing attribute types are never changed.
 */
export const reconcileDeveloperDraftWithProcessDm = (
  draft: DataModelDraft | null,
  processEntities: ProcessDmEntity[] | undefined
): DataModelDraft | null => {
  if (!draft || !processEntities?.length) {
    return draft
  }

  const reconciled: DataModelDraft = {
    new: [],
    existing: [...(draft.existing ?? [])],
    referenced: [],
  }

  const assign = (item: DataModelDraftItem, createBucket: DataModelDraftItem[]): void => {
    const { present, projectSourced } = matchAgainstProcessDm(item, processEntities)
    if (!present) {
      createBucket.push(item)
      return
    }
    reconciled.existing.push(projectSourced ? { ...item, fromProjectDataModel: true } : item)
  }

  for (const item of draft.new ?? []) {
    assign(item, reconciled.new)
  }
  for (const item of draft.referenced ?? []) {
    assign(item, reconciled.referenced)
  }

  return reconciled
}
